import { useEffect, useRef, useState } from "react";
import { Megaphone, Send, ImagePlus, X } from "lucide-react";
import { toast } from "sonner";
import api, { fmtErr } from "@/lib/api";
import { imgSrc } from "@/lib/imgSrc";

const AUDIENCES = [["all", "Everyone"], ["model", "Talent"], ["employer", "Brands"], ["access", "Full access"]];

export default function AdminAnnounce() {
  const [counts, setCounts] = useState(null);
  const [audience, setAudience] = useState("all");
  const [subject, setSubject] = useState("Solis Casting is now on Instagram");
  const [body, setBody] = useState(
    "Quick news from the office — Solis Casting has a new home on Instagram.\n\n" +
    "Follow @Soliscasting for fresh casting calls, new faces joining the board, and a look behind the scenes:\n" +
    "https://instagram.com/Soliscasting\n\n" +
    "Tag us on your shoots — we will be sharing our favorites."
  );
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [images, setImages] = useState([]);
  const [imgBusy, setImgBusy] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/admin/announce/audience").then((r) => setCounts(r.data)).catch(() => {});
  }, []);

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (images.length >= 4) return toast.error("Up to 4 images per announcement.");
    setImgBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads/image", fd);
      setImages((imgs) => [...imgs, data.url]);
      toast.success("Image added — it will show at the top of the email.");
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setImgBusy(false);
    }
  };

  const send = async (testOnly) => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/announce", { audience, subject, body, test_only: testOnly, images });
      if (data.queued) {
        toast.success(`Sending to ${data.total} member${data.total === 1 ? "" : "s"} in the background — track it below.`);
      } else {
        toast.success(testOnly
          ? "Test sent to the office inbox — check Solismagazine@gmail.com."
          : `Announcement sent to ${data.sent} of ${data.total} members${data.failed ? ` (${data.failed} failed)` : ""}.`);
      }
      setConfirm(false);
      if (!testOnly) { setSubject(""); setBody(""); setImages([]); }
      loadHistory();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  const [history, setHistory] = useState([]);
  const loadHistory = () => api.get("/admin/announcements").then((r) => setHistory(r.data)).catch(() => {});
  useEffect(() => { loadHistory(); }, []);

  const armThenSend = () => {
    if (!confirm) {
      setConfirm(true);
      setTimeout(() => setConfirm(false), 5000);
      return;
    }
    send(false);
  };

  return (
    <section className="panel p-8 mt-16" data-testid="admin-announce">
      <p className="overline-label !font-semibold !text-white/85 mb-2 flex items-center gap-2">
        <Megaphone size={14} /> Announcement
      </p>
      <p className="font-grotesk text-xs text-white/40 mb-8">
        One email to your members{counts ? ` — ${counts[audience]} in this audience` : ""}. Send yourself a test first; it lands in the office inbox.
      </p>
      <div className="flex border border-white/20 w-fit mb-6" data-testid="announce-audience">
        {AUDIENCES.map(([v, label]) => (
          <button key={v} data-testid={`announce-audience-${v}`} onClick={() => setAudience(v)}
            className={`px-4 py-2 text-[10px] uppercase tracking-[0.2em] transition-colors ${
              audience === v ? "bg-white text-black" : "text-white/60 hover:text-white"}`}>
            {label}{counts ? ` (${counts[v]})` : ""}
          </button>
        ))}
      </div>
      <input
        data-testid="announce-subject"
        value={subject}
        onChange={(e) => setSubject(e.target.value)}
        className="field mb-4"
        placeholder="Subject line"
      />
      <textarea
        data-testid="announce-body"
        value={body}
        onChange={(e) => setBody(e.target.value)}
        className="field min-h-[160px] mb-4"
        placeholder="Write the announcement — each line break becomes a paragraph, links become clickable"
      />
      <div className="mb-8" data-testid="announce-images">
        <div className="flex flex-wrap items-center gap-3">
          {images.map((src, i) => (
            <div key={src} className="relative" data-testid={`announce-image-${i}`}>
              <img src={imgSrc(src)} alt="" className="h-20 w-20 object-cover border border-white/15" />
              <button
                data-testid={`announce-image-remove-${i}`}
                onClick={() => setImages((imgs) => imgs.filter((_, j) => j !== i))}
                className="absolute -top-2 -right-2 bg-background border border-white/25 hover:border-accent hover:text-accent p-1 transition-colors"
                aria-label="Remove image"
              >
                <X size={11} />
              </button>
            </div>
          ))}
          {images.length < 4 && (
            <button type="button" data-testid="announce-image-add" onClick={() => fileRef.current?.click()} disabled={imgBusy} className="btn-outline !px-4 !py-2.5 inline-flex items-center gap-2 disabled:opacity-40">
              <ImagePlus size={14} /> {imgBusy ? "Uploading…" : images.length ? "Add another" : "Add image"}
            </button>
          )}
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={uploadImage} data-testid="announce-image-file" />
        </div>
        {images.length > 0 && (
          <p className="font-grotesk text-[10px] uppercase tracking-[0.12em] text-white/40 mt-3">
            Images land at the top of the email — keep them wide and light
          </p>
        )}
      </div>
      <div className="flex flex-wrap gap-3">
        <button data-testid="announce-test" onClick={() => send(true)} disabled={busy} className="btn-outline !px-5 !py-2.5 disabled:opacity-40">
          Send test to office inbox
        </button>
        <button
          data-testid="announce-send"
          onClick={armThenSend}
          disabled={busy}
          className={`${confirm ? "btn-primary" : "btn-outline"} !px-5 !py-2.5 inline-flex items-center gap-2 disabled:opacity-40`}
        >
          <Send size={13} /> {confirm ? "Tap again to confirm" : `Send to ${AUDIENCES.find(([v]) => v === audience)[1].toLowerCase()}`}
        </button>
      </div>
      {history.length > 0 && (
        <div className="mt-10" data-testid="announce-history">
          <p className="overline-label !text-[10px] mb-4">Recent sends</p>
          <div className="flex flex-col divide-y divide-white/10 border-t border-b border-white/10">
            {history.map((a) => (
              <div key={a.id} className="py-3 flex flex-wrap items-baseline gap-x-4 gap-y-1" data-testid={`announce-row-${a.id}`}>
                <p className="font-display italic text-base flex-1 min-w-[200px]">{a.subject}</p>
                <p className="font-grotesk text-[10px] uppercase tracking-[0.12em] text-white/45">
                  {a.audience} · {new Date(a.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                  {a.images?.length ? ` · ${a.images.length} img` : ""}
                </p>
                <p className={`font-grotesk text-[10px] uppercase tracking-[0.12em] ${a.status === "sending" ? "text-accent" : "text-white/70"}`} data-testid={`announce-status-${a.id}`}>
                  {a.status === "sending" ? `Sending… ${a.sent}/${a.total}` : `${a.sent} sent${a.failed ? ` · ${a.failed} failed` : ""}`}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
